import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface VoiceSettingsDialogProps {
  phoneNumberId: string;
  phoneNumberDisplay: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface VoiceSettings {
  id?: string;
  enable_greeting: boolean;
  greeting_message: string;
  forward_number: string;
  timeout_seconds: number;
  secondary_forward_number: string;
  secondary_timeout_seconds: number;
  enable_press_to_connect: boolean;
  press_to_connect_message: string;
  voicemail_message: string;
}

export function VoiceSettingsDialog({
  phoneNumberId,
  phoneNumberDisplay,
  open,
  onOpenChange,
}: VoiceSettingsDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enableSecondary, setEnableSecondary] = useState(false);

  const [settings, setSettings] = useState<VoiceSettings>({
    enable_greeting: true,
    greeting_message: "Thank you for calling Safeway Roofing and Chimney. Please hold while we connect you.",
    forward_number: "",
    timeout_seconds: 30,
    secondary_forward_number: "",
    secondary_timeout_seconds: 15,
    enable_press_to_connect: false,
    press_to_connect_message: "Press 1 to connect with a representative.",
    voicemail_message: "We're sorry, no one is available to take your call. Please leave a message after the beep.",
  });

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open, phoneNumberId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("voice_settings")
        .select("*")
        .eq("phone_number_id", phoneNumberId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings({
          id: data.id,
          enable_greeting: data.enable_greeting ?? true,
          greeting_message: data.greeting_message || settings.greeting_message,
          forward_number: data.forward_number || "",
          timeout_seconds: data.timeout_seconds || 30,
          secondary_forward_number: data.secondary_forward_number || "",
          secondary_timeout_seconds: data.secondary_timeout_seconds || 15,
          enable_press_to_connect: data.enable_press_to_connect ?? false,
          press_to_connect_message: data.press_to_connect_message || settings.press_to_connect_message,
          voicemail_message: data.voicemail_message || settings.voicemail_message,
        });
        setEnableSecondary(!!data.secondary_forward_number);
      }
    } catch (error) {
      console.error("Error loading voice settings:", error);
      toast({
        title: "Error",
        description: "Failed to load voice settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.forward_number) {
      toast({
        title: "Validation Error",
        description: "Primary forward number is required",
        variant: "destructive",
      });
      return;
    }

    if (enableSecondary && !settings.secondary_forward_number) {
      toast({
        title: "Validation Error",
        description: "Secondary forward number is required when enabled",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        phone_number_id: phoneNumberId,
        enable_greeting: settings.enable_greeting,
        greeting_message: settings.greeting_message,
        forward_number: settings.forward_number,
        timeout_seconds: settings.timeout_seconds,
        secondary_forward_number: enableSecondary ? settings.secondary_forward_number : null,
        secondary_timeout_seconds: enableSecondary ? settings.secondary_timeout_seconds : 15,
        enable_press_to_connect: settings.enable_press_to_connect,
        press_to_connect_message: settings.press_to_connect_message,
        voicemail_message: settings.voicemail_message,
      };

      if (settings.id) {
        const { error } = await supabase
          .from("voice_settings")
          .update(dataToSave)
          .eq("id", settings.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("voice_settings")
          .insert(dataToSave);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Voice settings saved successfully",
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving voice settings:", error);
      toast({
        title: "Error",
        description: "Failed to save voice settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Voice Settings: {phoneNumberDisplay}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Greeting Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="enable-greeting">Enable Greeting</Label>
                <Switch
                  id="enable-greeting"
                  checked={settings.enable_greeting}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, enable_greeting: checked })
                  }
                />
              </div>

              {settings.enable_greeting && (
                <div className="space-y-2">
                  <Label htmlFor="greeting-message">Greeting Message</Label>
                  <Textarea
                    id="greeting-message"
                    value={settings.greeting_message}
                    onChange={(e) =>
                      setSettings({ ...settings, greeting_message: e.target.value })
                    }
                    rows={3}
                    placeholder="Enter greeting message..."
                  />
                </div>
              )}
            </div>

            {/* Primary Forwarding Section */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold">Call Forwarding</h3>
              
              <div className="space-y-2">
                <Label htmlFor="forward-number">Primary Forward Number *</Label>
                <Input
                  id="forward-number"
                  type="tel"
                  value={settings.forward_number}
                  onChange={(e) =>
                    setSettings({ ...settings, forward_number: e.target.value })
                  }
                  placeholder="+15169120600"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeout">Ring Duration (seconds)</Label>
                <Input
                  id="timeout"
                  type="number"
                  min="10"
                  max="120"
                  value={settings.timeout_seconds}
                  onChange={(e) =>
                    setSettings({ ...settings, timeout_seconds: parseInt(e.target.value) || 30 })
                  }
                />
              </div>
            </div>

            {/* Secondary Forwarding Section */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="enable-secondary">Enable Secondary Forwarding</Label>
                <Switch
                  id="enable-secondary"
                  checked={enableSecondary}
                  onCheckedChange={setEnableSecondary}
                />
              </div>

              {enableSecondary && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="secondary-number">Secondary Forward Number *</Label>
                    <Input
                      id="secondary-number"
                      type="tel"
                      value={settings.secondary_forward_number}
                      onChange={(e) =>
                        setSettings({ ...settings, secondary_forward_number: e.target.value })
                      }
                      placeholder="+16317107600"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary-timeout">Ring Duration (seconds)</Label>
                    <Input
                      id="secondary-timeout"
                      type="number"
                      min="5"
                      max="60"
                      value={settings.secondary_timeout_seconds}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          secondary_timeout_seconds: parseInt(e.target.value) || 15,
                        })
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {/* Voicemail Section */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold">Voicemail</h3>
              
              <div className="space-y-2">
                <Label htmlFor="voicemail-message">Voicemail Message</Label>
                <Textarea
                  id="voicemail-message"
                  value={settings.voicemail_message}
                  onChange={(e) =>
                    setSettings({ ...settings, voicemail_message: e.target.value })
                  }
                  rows={3}
                  placeholder="Enter voicemail message..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Settings
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
